/* eslint-disable no-console */
/* eslint-disable max-depth */
/* eslint-disable max-statements */
/* eslint-disable complexity */

import { noop, isEmpty, isObject } from '@mindtickle/utils';

import { EMPTY_OBJECT_READONLY } from '../config/constants';

import QueueWithTimer from './QueueWithTimer';
import { logToMindTickle, timestamp } from '.';

const PRIMARY_INSTANCE_NAME = 'kamlesh';
const MIXPANEL_BEACON_API = '/logBrowserInteractionTime';
export class mixpanelManager {
  mixpanel = null;
  constructor() {
    this._params = {
      contextData: {},
      errorTracking: true,
    };

    this._hasInit = false;
    this._contextGetter = noop;
    const allMixpanelIds = process.env.MIXPANEL_PROJECT_ID.split('#');
    this.mixpanelProjects = [];
    if (allMixpanelIds.length > 1) {
      allMixpanelIds.shift();
      this.mixpanelProjects = allMixpanelIds.map(project => {
        const [, projectName] = project.split(':');
        return projectName;
      });
    }
    if (process.env.MIXPANEL_BATCHING) {
      this.queue = QueueWithTimer({
        batchSize: process.env.MIXPANEL_BATCH_SIZE,
        fireTime: process.env.MIXPANEL_THROTTLE_TIME,
        callback: events => {
          this.mixpanel.trackBatch(events);
          this.mixpanelProjects.forEach(projectName =>
            this.mixpanel[projectName].trackBatch(events)
          );
        },
      });
    }
  }

  setParams(newParams) {
    const receivedParams = isObject(newParams) ? newParams : EMPTY_OBJECT_READONLY;
    this._params = { ...this._params, ...receivedParams };
  }

  setContextGetter(contextGetterFn) {
    if (typeof contextGetterFn !== 'function') {
      return;
    }
    this._contextGetter = contextGetterFn;
  }

  get params() {
    return this._params;
  }

  get errorTracking() {
    return this._errorTracking;
  }

  get hasInit() {
    return this._hasInit;
  }

  getDerivedEventData(data) {
    let eventData;
    if (isEmpty(data.contextData)) {
      eventData = { ...this.params, ...data, ...this.params.contextData }; // use default context
    } else {
      eventData = { ...this.params, ...data, ...data.contextData }; // use context if recieved from withTracking
    }
    let customContext = this._contextGetter();
    customContext = customContext ? customContext : EMPTY_OBJECT_READONLY;
    eventData = { ...eventData, ...customContext };
    ['contextData', 'contextId', 'errorTracking'].forEach(key => {
      delete eventData[key];
    });
    eventData['Datadog Url'] =
      window.DD_RUM?.getSessionReplayLink?.() || 'Datadog Session Replay url not available';
    return eventData;
  }

  init = async (mixpanelId, userMeta) => {
    const { userId } = userMeta;
    this._userId = userId;
    if (!process.env.TRACKING) return;
    if (process.env.MIXPANEL_PROJECT_ID) {
      if (this.hasInit) {
        return;
      }

      try {
        console.log('Lazy loading Mixpanel Lib....');
        const mixpanelModule = await import(
          /* webpackChunkName: "mixpanel-js" */ '../library/mixpanel'
        );
        if (mixpanelModule && mixpanelModule.default) {
          const setupMixpanelGlobals = mixpanelModule.default;
          this.mixpanel = setupMixpanelGlobals(PRIMARY_INSTANCE_NAME);
        } else {
          console.error('Error: Mixpanel module or its default export not available.');
          return;
        }
      } catch (error) {
        console.error('Error loading Mixpanel Library:', error);
        return;
      }

      this._hasInit = true; //inits mixpanel
      let mixpanelIds = process.env.MIXPANEL_PROJECT_ID.split('#');
      const projectIdsCount = mixpanelIds.length;
      let mixpanelProjectId = process.env.MIXPANEL_PROJECT_ID;
      const config = {
        debug: process.env.MIXPANEL_DEBUG || false,
        persistence: 'localStorage',
        api_host: 'https://mt-fe-dc.mindtickle.com',
      };
      if (projectIdsCount > 1) {
        mixpanelProjectId = mixpanelIds[0].split(':')[0];
      }
      this.mixpanel = this.mixpanel.init(mixpanelProjectId, config);
      this.mixpanel.people.set({ id: userId });
      this.mixpanel.identify(mixpanelId);
      if (projectIdsCount > 1) {
        mixpanelIds.shift();
        mixpanelIds.forEach(mixpanelIds => {
          const [id, projectName] = mixpanelIds.split(':');
          this.mixpanel.init(id, config, projectName);
          this.mixpanel[projectName].people.set({ id: userId });
          this.mixpanel[projectName].identify(mixpanelId);
        });
      }
      if (process.env.MIXPANEL_BATCHING) {
        this.queue.start();
      }
    }
  };

  addToQueue = (event, data) => {
    data.time = timestamp() / 1000;
    this.queue.add({
      event,
      properties: data,
    });
  };

  _sendTrackEventUsingBeacon = (event, eventData) => {
    const distinct_id = this._userId;
    const dataToSend = JSON.stringify({
      distinct_id,
      event,
      properties: {
        ...eventData,
      },
    });
    if ('sendBeacon' in navigator) {
      navigator.sendBeacon(MIXPANEL_BEACON_API, dataToSend);
    }
  };

  track = (event, data, sendUsingBeacon = false) => {
    if (!this.hasInit) {
      return;
    }
    const eventData = this.getDerivedEventData(data);

    // This used for sending event when page is being unloaded as normal XHR request doesn't work for this case.
    if (sendUsingBeacon && process.env.MIXPANEL_PROJECT_ID) {
      this._sendTrackEventUsingBeacon(event, eventData);
      return;
    }

    if (process.env.MIXPANEL_BATCHING && process.env.MIXPANEL_PROJECT_ID) {
      this.addToQueue(event, eventData);
      logToMindTickle(event, eventData);
      return;
    }

    window.setTimeout(() => {
      if (process.env.MIXPANEL_PROJECT_ID) {
        this.mixpanel.track(event, eventData);
        this.mixpanelProjects.forEach(projectName =>
          this.mixpanel[projectName].track(event, eventData)
        );
      }
      logToMindTickle(event, eventData);
    }, 400);
  };
}

const mixpanelSingleton = new mixpanelManager();

export default mixpanelSingleton;
